(function () {
  'use strict';

  if (window.UnifiedConsent) {
    return;
  }

  const VERSION = '1.0.0';

  const state = {
    status: 'initializing',
    ready: false,
    analytics_allowed: false,
    marketing_allowed: false,
    preferences_allowed: false,
    sale_of_data_allowed: false,
    region: '',
    error: null,
    initialized_at: null,
    updated_at: null
  };

  const readyCallbacks = [];

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  function nowIso() {
    return new Date()
      .toISOString();
  }

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function log(
    message,
    data
  ) {
    if (!isDebugEnabled()) {
      return;
    }

    console.info(
      '[Unified Consent] ' +
        message,
      data || {}
    );
  }

  /*
   * ------------------------------------------------------------
   * EVENT EMISSION
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        // Diagnostic fallback below.
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                nowIso(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ERROR REPORTING
   * ------------------------------------------------------------
   */

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      try {
        return window.UnifiedErrors.report(
          severity,
          code,
          message,
          details || {}
        );
      } catch (error) {
        // Console fallback below.
      }
    }

    const method =
      severity === 'error' ||
      severity === 'fatal'
        ? 'error'
        : 'warn';

    console[method](
      '[Unified Consent] ' +
        message,
      details || {}
    );

    return null;
  }

  /*
   * ------------------------------------------------------------
   * SHOPIFY PRIVACY API
   * ------------------------------------------------------------
   */

  function getPrivacyApi() {
    if (
      window.Shopify &&
      window.Shopify.customerPrivacy
    ) {
      return window.Shopify
        .customerPrivacy;
    }

    return null;
  }

  function safeCall(
    api,
    methodName,
    fallback
  ) {
    if (
      !api ||
      typeof api[methodName] !==
        'function'
    ) {
      return fallback;
    }

    try {
      return api[
        methodName
      ]();
    } catch (error) {
      reportError(
        'warning',
        'consent_method_failed',
        'Customer Privacy API method failed.',
        {
          method:
            methodName,

          error:
            error.message
        }
      );

      return fallback;
    }
  }

  /*
   * ------------------------------------------------------------
   * STATE REFRESH
   *
   * This is the ONLY function that actively
   * reads the Shopify Customer Privacy API.
   *
   * Public ...Allowed() methods only read
   * the cached state.
   * ------------------------------------------------------------
   */

  function refreshState(
    source
  ) {
    const api =
      getPrivacyApi();

    if (!api) {
      state.ready =
        false;

      state.status =
        'unavailable';

      state.analytics_allowed =
        false;

      state.marketing_allowed =
        false;

      state.preferences_allowed =
        false;

      state.sale_of_data_allowed =
        false;

      state.updated_at =
        nowIso();

      emit(
        'consent_unavailable',
        {
          source:
            source ||
            'unknown'
        }
      );

      return clone(
        state
      );
    }

    state.ready =
      true;

    state.status =
      'ready';

    state.analytics_allowed =
      safeCall(
        api,
        'analyticsProcessingAllowed',
        false
      ) === true;

    state.marketing_allowed =
      safeCall(
        api,
        'marketingAllowed',
        false
      ) === true;

    state.preferences_allowed =
      safeCall(
        api,
        'preferencesProcessingAllowed',
        false
      ) === true;

    state.sale_of_data_allowed =
      safeCall(
        api,
        'saleOfDataAllowed',
        false
      ) === true;

    state.region =
      safeCall(
        api,
        'getRegion',
        ''
      ) || '';

    state.updated_at =
      nowIso();

    emit(
      'consent_state_updated',
      {
        source:
          source ||
          'unknown',

        analytics_allowed:
          state.analytics_allowed,

        marketing_allowed:
          state.marketing_allowed,

        preferences_allowed:
          state.preferences_allowed,

        sale_of_data_allowed:
          state.sale_of_data_allowed,

        region:
          state.region
      }
    );

    log(
      'Consent state updated',
      {
        source:
          source ||
          'unknown',

        analyticsAllowed:
          state.analytics_allowed,

        marketingAllowed:
          state.marketing_allowed,

        preferencesAllowed:
          state.preferences_allowed,

        saleOfDataAllowed:
          state.sale_of_data_allowed,

        region:
          state.region
      }
    );

    return clone(
      state
    );
  }

  /*
   * ------------------------------------------------------------
   * READY CALLBACKS
   * ------------------------------------------------------------
   */

  function flushReadyCallbacks() {
    const callbacks =
      readyCallbacks.splice(
        0,
        readyCallbacks.length
      );

    callbacks.forEach(
      function (
        callback
      ) {
        try {
          callback(
            clone(
              state
            )
          );
        } catch (error) {
          reportError(
            'warning',
            'consent_ready_callback_failed',
            'A Unified Consent ready callback failed.',
            {
              error:
                error.message
            }
          );
        }
      }
    );
  }

  function finalizeReady(
    source
  ) {
    if (
      !state.initialized_at
    ) {
      state.initialized_at =
        nowIso();
    }

    refreshState(
      source ||
      'initialization'
    );

    if (
      state.ready
    ) {
      emit(
        'consent_ready',
        {
          analytics_allowed:
            state.analytics_allowed,

          marketing_allowed:
            state.marketing_allowed,

          region:
            state.region
        }
      );

      flushReadyCallbacks();
    }
  }

  /*
   * ------------------------------------------------------------
   * CONSENT CHANGE
   * ------------------------------------------------------------
   */

  function handleConsentChange(
    event
  ) {
    refreshState(
      'visitorConsentCollected'
    );

    emit(
      'consent_changed',
      {
        detail:
          event &&
          event.detail
            ? clone(
                event.detail
              )
            : {},

        analytics_allowed:
          state.analytics_allowed,

        marketing_allowed:
          state.marketing_allowed
      }
    );
  }

  /*
   * ------------------------------------------------------------
   * SHOPIFY API LOADING
   * ------------------------------------------------------------
   */

  function loadCustomerPrivacyApi() {
    state.status =
      'loading';

    /*
     * API may already be available.
     */

    if (
      window.Shopify &&
      window.Shopify.customerPrivacy
    ) {
      finalizeReady(
        'already_available'
      );

      return;
    }

    /*
     * Shopify feature loader is required.
     */

    if (
      !window.Shopify ||
      typeof window.Shopify.loadFeatures !==
        'function'
    ) {
      state.status =
        'unavailable';

      state.ready =
        false;

      state.error = {
        code:
          'shopify_load_features_unavailable'
      };

      state.updated_at =
        nowIso();

      reportError(
        'error',
        'shopify_load_features_unavailable',
        'Shopify.loadFeatures is unavailable.',
        {}
      );

      emit(
        'consent_load_failed',
        {
          reason:
            'shopify_load_features_unavailable'
        }
      );

      return;
    }

    try {
      window.Shopify.loadFeatures(
        [
          {
            name:
              'consent-tracking-api',

            version:
              '0.1'
          }
        ],

        function (error) {
          if (error) {
            state.status =
              'error';

            state.ready =
              false;

            state.error = {
              code:
                'consent_api_load_failed',

              message:
                error.message ||
                String(error)
            };

            state.updated_at =
              nowIso();

            reportError(
              'error',
              'consent_api_load_failed',
              'Shopify Customer Privacy API failed to load.',
              {
                error:
                  state.error
                    .message
              }
            );

            emit(
              'consent_load_failed',
              {
                reason:
                  'consent_api_load_failed'
              }
            );

            return;
          }

          state.error =
            null;

          finalizeReady(
            'shopify_load_features'
          );
        }
      );
    } catch (error) {
      state.status =
        'error';

      state.ready =
        false;

      state.error = {
        code:
          'consent_api_exception',

        message:
          error.message
      };

      state.updated_at =
        nowIso();

      reportError(
        'error',
        'consent_api_exception',
        'Shopify Customer Privacy API loading threw an exception.',
        {
          error:
            error.message
        }
      );

      emit(
        'consent_load_failed',
        {
          reason:
            'consent_api_exception'
        }
      );
    }
  }

  /*
   * ------------------------------------------------------------
   * READY API
   * ------------------------------------------------------------
   */

  function whenReady(
    callback
  ) {
    if (
      typeof callback !==
        'function'
    ) {
      return false;
    }

    if (
      state.ready
    ) {
      callback(
        clone(
          state
        )
      );

      return true;
    }

    readyCallbacks.push(
      callback
    );

    return true;
  }

  function isReady() {
    return (
      state.ready ===
      true
    );
  }

  /*
   * ------------------------------------------------------------
   * CONSENT READ METHODS
   *
   * IMPORTANT:
   * These methods DO NOT refresh Shopify state.
   *
   * They only read the state cached by:
   * - initial API load
   * - explicit refresh()
   * - visitorConsentCollected
   *
   * This prevents Dashboard / Health /
   * Provider Manager status checks from
   * creating consent_state_updated spam.
   * ------------------------------------------------------------
   */

  function analyticsAllowed() {
    return (
      state.ready === true &&
      state.analytics_allowed ===
        true
    );
  }

  function marketingAllowed() {
    return (
      state.ready === true &&
      state.marketing_allowed ===
        true
    );
  }

  function preferencesAllowed() {
    return (
      state.ready === true &&
      state.preferences_allowed ===
        true
    );
  }

  function saleOfDataAllowed() {
    return (
      state.ready === true &&
      state.sale_of_data_allowed ===
        true
    );
  }

  /*
   * ------------------------------------------------------------
   * STATE ACCESS
   * ------------------------------------------------------------
   */

  function getState() {
    return clone(
      state
    );
  }

  function getApi() {
    return getPrivacyApi();
  }

  /*
   * ------------------------------------------------------------
   * SHOPIFY CONSENT EVENT
   * ------------------------------------------------------------
   */

  document.addEventListener(
    'visitorConsentCollected',
    handleConsentChange
  );

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedConsent = {
    version:
      VERSION,

    isReady:
      isReady,

    whenReady:
      whenReady,

    refresh:
      refreshState,

    analyticsAllowed:
      analyticsAllowed,

    marketingAllowed:
      marketingAllowed,

    preferencesAllowed:
      preferencesAllowed,

    saleOfDataAllowed:
      saleOfDataAllowed,

    getState:
      getState,

    getApi:
      getApi
  };

  /*
   * ------------------------------------------------------------
   * INITIALIZATION
   * ------------------------------------------------------------
   */

  loadCustomerPrivacyApi();

  log(
    'Consent Manager initialized',
    {
      version:
        VERSION
    }
  );
})();